import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/chitconnect-logo-blue.png';
import { apiFetch } from '../lib/api';

export default function PremiumReport() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const gid = useMemo(() => parseInt(groupId, 10), [groupId]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [memberships, setMemberships] = useState([]);
  const [membershipIndex, setMembershipIndex] = useState('');

  // What-if inputs
  const [targetBid, setTargetBid] = useState('');
  const [targetPeriod, setTargetPeriod] = useState('');

  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Load eligible premium memberships (reuse AutoBid eligibility endpoint)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      setMsg('');
      try {
        const res = await apiFetch('/api/auto-bids/eligible/' + gid);
        const json = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setErr(json?.error || 'Premium membership required');
          setMemberships([]);
          return;
        }
        const list = Array.isArray(json?.memberships) ? json.memberships : [];
        setMemberships(list);
        if (list.length > 0) {
          setMembershipIndex((prev) => (prev ? prev : String(list[0].membership_index ?? 1)));
        } else {
          setErr('Premium membership required');
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to load memberships');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [gid]);

  async function loadReport({ keepMsg = false } = {}) {
    setErr('');
    if (!keepMsg) setMsg('');
    try {
      const qs = new URLSearchParams();
      qs.set('membership_index', membershipIndex || '1');
      if (String(targetBid).trim() !== '') qs.set('target_bid', String(targetBid).trim());
      if (String(targetPeriod).trim() !== '') qs.set('target_period', String(targetPeriod).trim());

      const res = await apiFetch(`/api/premium/report/${gid}?${qs.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(json?.error || 'Failed to load report');
        setReport(null);
        return;
      }
      setReport(json);

      // If backend picked a default target period and UI has none selected, adopt it
      if (!String(targetPeriod).trim() && json?.target_period) {
        setTargetPeriod(String(json.target_period));
      }
    } catch (e) {
      setErr(e?.message || 'Failed to load report');
      setReport(null);
    }
  }

  useEffect(() => {
    if (!membershipIndex) return;
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipIndex, gid]);

  async function downloadPdf() {
    if (!membershipIndex) return;
    setDownloading(true);
    setErr('');
    setMsg('');
    try {
      const qs = new URLSearchParams();
      qs.set('membership_index', membershipIndex || '1');
      if (String(targetBid).trim() !== '') qs.set('target_bid', String(targetBid).trim());
      if (String(targetPeriod).trim() !== '') qs.set('target_period', String(targetPeriod).trim());

      const res = await apiFetch(`/api/premium/report/${gid}/pdf?${qs.toString()}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setErr(j?.error || 'Failed to download PDF');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `premium_bid_impact_group_${gid}_m${membershipIndex}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg('✅ PDF downloaded');
    } catch (e) {
      setErr(e?.message || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  }

  const rows = report?.rows || [];
  const currency = report?.currency || { code: null, symbol: '' };
  const numFmt = useMemo(() => new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }), []);

  const fmtMoney = (v) => {
    const n = Number(v || 0);
    const val = numFmt.format(Number.isFinite(n) ? n : 0);
    return currency.symbol ? `${currency.symbol} ${val}` : val;
  };

  const net = report?.net_extra_receive_or_pay;
  const netNum = net === null || net === undefined ? null : Number(net);

  const srcLabel = (s) => {
    switch (s) {
      case 'target_bid': return 'Your bid';
      case 'final_highest_bid': return 'Past final';
      case 'starting_bid': return 'LuckyDip start';
      case 'obp': return 'OBP';
      default: return '—';
    }
  };

  return (
    <div className="main-content">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Premium Bid Impact Report</h2>
        <button onClick={() => navigate(-1)}>← Back</button>
      </div>
      <p style={{ marginTop: 8, opacity: 0.8 }}>
        Default is the current bidding period (or next). If that period is Organiser Benefit (OBP), it automatically uses the next period.
        You can also choose a later period.
      </p>

      {loading ? (
        <div className="group-card">Loading…</div>
      ) : (
        <>
          {err ? <p className="error-text">{err}</p> : null}
          {msg ? <p style={{ color: '#065f46', fontWeight: 700 }}>{msg}</p> : null}

          <div className="group-card">
            <div className="premrep-grid">
              <div>
                <div className="premrep-label">Premium membership</div>
                <select
                  className="premrep-control"
                  value={membershipIndex}
                  onChange={(e) => setMembershipIndex(e.target.value)}
                >
                  {memberships.map((m) => (
                    <option key={String(m.membership_index)} value={String(m.membership_index)}>
                      Membership #{m.membership_index}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="premrep-label">Coming bidding period</div>
                <select
                  className="premrep-control"
                  value={targetPeriod}
                  onChange={(e) => setTargetPeriod(e.target.value)}
                >
                  {(report?.candidate_periods || [report?.target_period].filter(Boolean)).map((p) => (
                    <option key={String(p)} value={String(p)}>
                      Period #{p}{p === report?.default_target_period ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="premrep-label">Your target bid (what-if you win)</div>
                <input
                  className="premrep-control"
                  type="number"
                  inputMode="decimal"
                  value={targetBid}
                  onChange={(e) => setTargetBid(e.target.value)}
                  placeholder="e.g., 2100"
                />
              </div>

              <div className="premrep-actions">
                <button onClick={() => loadReport()}>Calculate</button>
                <button onClick={downloadPdf} disabled={downloading}>
                  {downloading ? 'Downloading…' : 'Download PDF'}
                </button>
              </div>
            </div>
            <div className="premrep-hint">
              Calculation model: pay each period = contribution − (amount_used ÷ duration). Past periods use final highest bid. Future periods assume LuckyDip using starting bids. OBP is full contribution. Target period uses your entered bid.
            </div>
          </div>

          {report ? (
            <div className="group-card premrep-summary">
              <div>
                <div className="premrep-kpiLabel">Pot total</div>
                <div className="premrep-kpiValue">{fmtMoney(report.group?.pot_total)}</div>
              </div>
              <div>
                <div className="premrep-kpiLabel">Total pay (whole duration)</div>
                <div className="premrep-kpiValue">{fmtMoney(report.total_pay_for_group)}</div>
              </div>
              <div>
                <div className="premrep-kpiLabel">Receive if win target</div>
                <div className="premrep-kpiValue">{report.receive_if_win_target !== null ? fmtMoney(report.receive_if_win_target) : '—'}</div>
              </div>
              <div>
                <div className="premrep-kpiLabel">Extra receive / pay</div>
                <div className={`premrep-kpiValue ${netNum === null ? '' : (netNum >= 0 ? 'good' : 'bad')}`}>
                  {netNum === null ? 'Enter a bid' : fmtMoney(report.net_extra_receive_or_pay)}
                </div>
              </div>
            </div>
          ) : null}

          <div className="group-card-list">
            {rows.map((r) => {
              const isTarget = Number(r.period_number) === Number(report?.target_period);
              return (
                <div
                  key={r.period_number}
                  className="group-card"
                  style={isTarget ? { border: '2px solid #0A3E75' } : undefined}
                >
                  <div className="premrep-rowTop">
                    <div className="premrep-rowTitle">Period #{r.period_number}</div>
                    <div className={`premrep-chip ${r.is_obp ? 'obp' : r.final_highest_bid !== null ? 'past' : 'future'}`}>
                      {r.is_obp ? 'OBP' : (r.final_highest_bid !== null ? 'Past' : 'LuckyDip')}
                    </div>
                  </div>

                  <div className="premrep-rowGrid">
                    <div>
                      <div className="premrep-miniLabel">Contribution</div>
                      <div className="premrep-miniValue">{fmtMoney(r.contribution)}</div>
                    </div>
                    <div>
                      <div className="premrep-miniLabel">Amount used</div>
                      <div className="premrep-miniValue">{fmtMoney(r.amount_used)}</div>
                      <div className="premrep-miniNote">{srcLabel(r.amount_source)}{isTarget ? ' (selected)' : ''}</div>
                    </div>
                    <div>
                      <div className="premrep-miniLabel">Share (amount ÷ duration)</div>
                      <div className="premrep-miniValue">{fmtMoney(r.share_per_person)}</div>
                    </div>
                    <div>
                      <div className="premrep-miniLabel">Pay this period</div>
                      <div className="premrep-miniValue">{fmtMoney(r.pay_amount)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
